// Admin configuration: the editable label catalog plus a read-only view of the
// configured brands and lifecycle statuses. (SLA targets live in their own
// "SLAs" section; statuses/brands/categories are editable via the backend.)
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { brand as themeBrand } from "../../../config/theme";
import { useRolesStore } from "../../../store/admin/rolesStore";
import { getError } from "../../../utils/getError";
import {
  getSettings, updateSettings, selectableStages, DEV_STAGE_META, DEV_STAGE_ORDER,
  AI_FIRST_REPLY_MODELS, DEFAULT_AI_FIRST_REPLY,
  type ServiceDeskSettings, type LabelOption, type Appearance,
  type AiAgentSettings, type AiFirstReplySettings, type TicketPriority,
  type WhatsappGroupsSettings,
  type StageOption, type DevStageId,
} from "../services/serviceDeskService";
import { DeskLoadErrorPanel, useDeskLoadError } from "../components/DeskLoadError";
import {
  getWhatsappStatus,
  connectorLabel,
  CONNECTOR_SOURCE_LABEL,
  isValidReplyTemplate,
  type WaConnectorResolution,
} from "../services/whatsappGroupsService";

const DEFAULT_APPEARANCE: Appearance = {
  primaryColor: "",
  headerTitle: "Mesa de Servicio",
  headerSubtitle: "Service Desk",
  logoUrl: "",
  portalIntro: "",
};

// Debe coincidir con DEFAULT_AI_AGENT del backend (serviceDeskConstants.js).
const DEFAULT_AI_AGENT: AiAgentSettings = {
  enabled: false,
  requireApproval: true,
  maxAttempts: 2,
  leaseMinutes: 120,
  eligibleLabels: ["bug", "feature_request", "incident"],
  eligibleCategories: ["technical", "feature"],
  minPriority: null,
};

// Debe coincidir con DEFAULT_WHATSAPP_GROUPS del backend (whatsapp/waConstants.js).
const DEFAULT_WHATSAPP_GROUPS: WhatsappGroupsSettings = {
  enabled: false,
  connector: "auto",
  requireContactConfirmation: true,
  askUnknownParticipants: true,
  replyTemplate: null,
};

// Listas que se editan como texto libre (una por línea o separadas por comas).
// El servidor las vuelve a validar y deduplicar: esto sólo recorta y quita
// vacíos para que un salto de línea de más no mande una entrada en blanco.
function parseList(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function SettingsSection() {
  const { t } = useTranslation("serviceDesk");
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [reposText, setReposText] = useState("");
  const { roles, fetchRoles } = useRolesStore();
  // Un no agente ve 403 aquí también; sin capturarlo la pantalla se quedaba en
  // "Cargando…" y nunca se leía a quién pedirle el alta.
  const { error: loadError, capture } = useDeskLoadError();
  // Qué conector de WhatsApp aplica AHORA y por qué (#716 · D8). Es un extra
  // informativo: si la lectura falla, la tarjeta sigue funcionando sin él.
  const [waResolution, setWaResolution] = useState<WaConnectorResolution | null>(null);
  const refreshWaResolution = () =>
    getWhatsappStatus()
      .then((st) => setWaResolution(st.connectorResolution || null))
      .catch(() => setWaResolution(null));
  useEffect(() => {
    refreshWaResolution();
  }, []);
  useEffect(() => {
    getSettings()
      .then((s) => {
        setSettings(s);
        setReposText((s.githubRepos || []).join("\n"));
      })
      .catch(capture);
  }, [capture]);
  useEffect(() => { fetchRoles().catch(() => {}); }, [fetchRoles]);

  const save = async (patch: Partial<ServiceDeskSettings>) => {
    setSaving(true);
    try {
      const updated = await updateSettings(patch);
      setSettings(updated);
      if (patch.whatsappGroups) refreshWaResolution();
      Swal.fire({ icon: "success", title: t("settings.saved"), timer: 1200, showConfirmButton: false });
    } catch (err) {
      // Guardar exige rol de agente de la mesa (y admin reseller para los roles
      // de agente): sin este aviso el 403 quedaba en una promesa sin capturar.
      Swal.fire({ icon: "error", title: t("settings.saveError"), text: getError(err) });
    } finally {
      setSaving(false);
    }
  };

  const card = "bg-surface border-line";
  const text = "text-content";
  const muted = "text-content-muted";
  const txtCls = `flex-1 rounded-lg border px-2 py-1.5 text-sm !bg-app !border-line !text-content placeholder:!text-content-faint`;

  if (loadError) return <DeskLoadErrorPanel error={loadError} />;
  if (!settings) return <div className="p-8 text-center text-gray-400">{t("dashboard.loading")}</div>;

  const appearance: Appearance = { ...DEFAULT_APPEARANCE, ...(settings.appearance || {}) };
  const setAppearance = (patch: Partial<Appearance>) =>
    setSettings((s) => s && ({ ...s, appearance: { ...DEFAULT_APPEARANCE, ...(s.appearance || {}), ...patch } }));

  const slaBreachEmails = (settings.slaBreachNotifyEmails || []).join("\n");
  const setSlaBreachEmails = (text: string) => {
    const emails = text.split("\n").map((e) => e.trim()).filter(Boolean);
    setSettings((s) => s && { ...s, slaBreachNotifyEmails: emails });
  };
  const agentRoleIds: string[] = settings.agentRoleIds || [];
  const setAgentRoleIds = (next: string[]) => setSettings((s) => s && ({ ...s, agentRoleIds: next }));

  // Etapas del ciclo de desarrollo (#559). Los ids son fijos; se configuran el
  // nombre, el color y si la mesa usa o no cada etapa.
  const stages: StageOption[] = settings.stages?.length
    ? settings.stages
    : DEV_STAGE_ORDER.map((id) => ({ id, ...DEV_STAGE_META[id], enabled: true }));
  const patchStage = (id: DevStageId, patch: Partial<StageOption>) =>
    setSettings((s) =>
      s && {
        ...s,
        stages: (s.stages?.length
          ? s.stages
          : DEV_STAGE_ORDER.map((sid) => ({ id: sid, ...DEV_STAGE_META[sid], enabled: true }))
        ).map((st) => (st.id === id ? { ...st, ...patch } : st)),
      },
    );
  // «Recibido» y «Resuelto» no se pueden apagar: sin la primera un ticket no
  // tiene línea de tiempo y sin la última nunca puede terminarla.
  const REQUIRED_STAGES: DevStageId[] = ["received", "resolved"];

  const aiAgent: AiAgentSettings = { ...DEFAULT_AI_AGENT, ...(settings.aiAgent || {}) };
  const setAiAgent = (patch: Partial<AiAgentSettings>) =>
    setSettings((s) => s && ({ ...s, aiAgent: { ...DEFAULT_AI_AGENT, ...(s.aiAgent || {}), ...patch } }));
  const toggleIn = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  // Respuesta inmediata con IA (#508). Bloque SEPARADO del agente de código:
  // éste sí le escribe al cliente, así que se enciende y se apaga aparte.
  const aiFirstReply: AiFirstReplySettings = {
    ...DEFAULT_AI_FIRST_REPLY,
    ...(settings.aiFirstReply || {}),
  };
  const setAiFirstReply = (patch: Partial<AiFirstReplySettings>) =>
    setSettings((s) => s && ({
      ...s,
      aiFirstReply: { ...DEFAULT_AI_FIRST_REPLY, ...(s.aiFirstReply || {}), ...patch },
    }));

  const whatsappGroups: WhatsappGroupsSettings = {
    ...DEFAULT_WHATSAPP_GROUPS,
    ...(settings.whatsappGroups || {}),
  };
  const setWhatsappGroups = (patch: Partial<WhatsappGroupsSettings>) =>
    setSettings(
      (s) =>
        s && {
          ...s,
          whatsappGroups: { ...DEFAULT_WHATSAPP_GROUPS, ...(s.whatsappGroups || {}), ...patch },
        },
    );

  const addLabel = async () => {
    const { value } = await Swal.fire({
      title: t("settings.labels.modalTitle"),
      html: '<input id="l-id" class="swal2-input" placeholder="id (ej. urgente)"><input id="l-label" class="swal2-input" placeholder="Etiqueta visible"><input id="l-color" class="swal2-input" placeholder="#3B82F6" value="#3B82F6">',
      preConfirm: () => ({
        id: (document.getElementById("l-id") as HTMLInputElement)?.value,
        label: (document.getElementById("l-label") as HTMLInputElement)?.value,
        color: (document.getElementById("l-color") as HTMLInputElement)?.value,
      }),
      showCancelButton: true,
      confirmButtonColor: themeBrand.primary,
    });
    if (value?.id && value?.label) {
      save({ labels: [...(settings.labels || []), value as LabelOption] });
    }
  };

  const removeLabel = (id: string) => save({ labels: (settings.labels || []).filter((l) => l.id !== id) });

  return (
    <div className="p-6">
      <h1 className={`mb-5 text-xl font-bold ${text}`}>{t("settings.title")}</h1>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("settings.appearance.title")}</h2>
          <p className={`mb-4 text-xs ${muted}`}>{t("settings.appearance.description")}</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>{t("settings.appearance.primaryColor")}</label>
              <input
                type="color"
                className="h-8 w-12 cursor-pointer rounded border border-gray-300 bg-transparent"
                value={appearance.primaryColor || "#4F46E5"}
                onChange={(e) => setAppearance({ primaryColor: e.target.value })}
              />
              <input
                className={txtCls}
                placeholder={t("settings.appearance.colorPlaceholder")}
                value={appearance.primaryColor || ""}
                onChange={(e) => setAppearance({ primaryColor: e.target.value })}
              />
            </div>
            <Field label={t("settings.appearance.headerTitle")} value={appearance.headerTitle || ""} onChange={(v) => setAppearance({ headerTitle: v })} placeholder={t("settings.appearance.headerTitlePlaceholder")} muted={muted} txtCls={txtCls} />
            <Field label={t("settings.appearance.headerSubtitle")} value={appearance.headerSubtitle || ""} onChange={(v) => setAppearance({ headerSubtitle: v })} placeholder={t("settings.appearance.headerSubtitlePlaceholder")} muted={muted} txtCls={txtCls} />
            <Field label={t("settings.appearance.logoUrl")} value={appearance.logoUrl || ""} onChange={(v) => setAppearance({ logoUrl: v })} placeholder={t("settings.appearance.logoUrlPlaceholder")} muted={muted} txtCls={txtCls} />
            <div className="flex items-start gap-3">
              <label className={`w-32 pt-2 text-xs ${muted}`}>{t("settings.appearance.portalIntro")}</label>
              <textarea
                className={`${txtCls} h-16 resize-none`}
                placeholder={t("settings.appearance.portalIntroPlaceholder")}
                value={appearance.portalIntro || ""}
                onChange={(e) => setAppearance({ portalIntro: e.target.value })}
              />
            </div>
          </div>
          <button
            onClick={() => save({ appearance })}
            disabled={saving}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 hover:opacity-90"
            style={{ backgroundColor: appearance.primaryColor || themeBrand.primary }}
          >
            {t("settings.appearance.save")}
          </button>
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("settings.publicSite.title")}</h2>
          <p className={`mb-4 text-xs ${muted}`}>{t("settings.publicSite.description")}</p>
          <div className={`flex items-center justify-between rounded-lg border px-3 py-2 border-line`}>
            <code className={`text-xs ${muted}`}>/soporte</code>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/soporte`)}
                className="text-xs text-gray-400 hover:underline"
              >
                {t("settings.publicSite.copyLink")}
              </button>
              <a
                href="/soporte"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: appearance.primaryColor || themeBrand.primary }}
              >
                <i className="ri-external-link-line" /> {t("settings.publicSite.openSite")}
              </a>
            </div>
          </div>
        </div>

        {/* Agentes de la mesa — settings.agentRoleIds. Quien tiene uno de estos
            roles puede hacer triage (etiquetas, categoría, prioridad, marca:
            lo que el agente IA congela al aprobar) y guardar esta pantalla.
            Solo el admin reseller puede cambiar la lista. */}
        <div className={`rounded-xl border p-5 ${card}`}>
          {/* El 403 del gate (AGENT_ONLY_MESSAGE) manda al usuario a "Roles de
              agente": el alias va en el título para que quien llegue leyendo
              ese mensaje encuentre esta tarjeta. */}
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>Agentes de la mesa (roles de agente)</h2>
          <p className={`mb-4 text-xs ${muted}`}>
            Roles que pueden hacer triage (etiquetas, categoría, prioridad y marca) y guardar esta
            configuración. El <strong>admin reseller</strong> siempre es agente, aunque no elijas
            ninguno: es el valor por omisión en una instalación nueva. Solo el admin reseller puede
            cambiar esta lista.
          </p>
          {roles.length === 0 ? (
            <p className={`text-xs ${muted}`}>No se pudieron cargar los roles.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {roles.map((r) => {
                const active = agentRoleIds.includes(r.id);
                return (
                  <button
                    key={r.id}
                    onClick={() => setAgentRoleIds(toggleIn(agentRoleIds, r.id))}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium ${active ? "text-white" : "text-gray-500"}`}
                    style={{ backgroundColor: active ? themeBrand.primary : "#9ca3af1a" }}
                  >
                    {r.name}
                  </button>
                );
              })}
            </div>
          )}
          <button
            onClick={() => save({ agentRoleIds })}
            disabled={saving || roles.length === 0}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: themeBrand.primary }}
          >
            Guardar agentes
          </button>
        </div>

        {/* Agente IA — el runner local (sd-agent.mjs) lee esta configuración
            desde /serviceDesk/ai/queue en cada corrida. */}
        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>Agente IA</h2>
          <p className={`mb-4 text-xs ${muted}`}>
            Toma tickets nuevos, los analiza, propone una solución, la implementa y abre un Pull
            Request para revisión humana. Nunca responde al cliente ni hace merge.
          </p>

          <label className={`mb-4 flex items-center gap-2 text-sm ${text}`}>
            <input
              type="checkbox"
              checked={aiAgent.enabled}
              onChange={(e) => setAiAgent({ enabled: e.target.checked })}
            />
            Activar el agente IA en esta mesa
          </label>

          <label className={`mb-1 flex items-center gap-2 text-sm ${text}`}>
            <input
              type="checkbox"
              checked={aiAgent.requireApproval}
              onChange={(e) => setAiAgent({ requireApproval: e.target.checked })}
            />
            Exigir aprobación del staff antes de que el agente tome un ticket
          </label>
          <p className={`mb-4 ml-6 text-xs ${muted}`}>
            Recomendado. Los tickets del portal público siempre requieren aprobación, aunque
            desactives esta opción. Solo el staff de la mesa puede guardar estos ajustes.
          </p>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>Intentos máximos</label>
              <input
                type="number" min={1} max={10} className={txtCls}
                value={aiAgent.maxAttempts}
                onChange={(e) => setAiAgent({ maxAttempts: Number(e.target.value) || 1 })}
              />
            </div>
            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>Lease (minutos)</label>
              <input
                type="number" min={15} max={480} className={txtCls}
                value={aiAgent.leaseMinutes}
                onChange={(e) => setAiAgent({ leaseMinutes: Number(e.target.value) || 15 })}
              />
            </div>
            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>Prioridad mínima</label>
              <select
                className={txtCls}
                value={aiAgent.minPriority || ""}
                onChange={(e) => setAiAgent({ minPriority: (e.target.value || null) as TicketPriority | null })}
              >
                <option value="">Todas</option>
                <option value="low">Baja</option>
                <option value="medium">Media</option>
                <option value="high">Alta</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>

            <div>
              <span className={`mb-1.5 block text-xs ${muted}`}>Etiquetas elegibles</span>
              <div className="flex flex-wrap gap-2">
                {settings.labels.map((l) => {
                  const active = aiAgent.eligibleLabels.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      onClick={() => setAiAgent({ eligibleLabels: toggleIn(aiAgent.eligibleLabels, l.id) })}
                      className="rounded-full px-2.5 py-1 text-xs font-medium"
                      style={{ color: active ? "#fff" : l.color, backgroundColor: active ? l.color : `${l.color}1a` }}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <span className={`mb-1.5 block text-xs ${muted}`}>Categorías elegibles</span>
              <div className="flex flex-wrap gap-2">
                {settings.categories.map((c) => {
                  const active = aiAgent.eligibleCategories.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => setAiAgent({ eligibleCategories: toggleIn(aiAgent.eligibleCategories, c.id) })}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${active ? "text-white" : "text-gray-500"}`}
                      style={{ backgroundColor: active ? themeBrand.primary : "#9ca3af1a" }}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <button
            onClick={() => save({ aiAgent })}
            disabled={saving}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: themeBrand.primary }}
          >
            Guardar agente IA
          </button>
        </div>

        {/* Respuesta inmediata con IA — la escribe el trigger de creación de
            ticket (onServiceDeskTicketCreated), no un runner local. */}
        <div className={`rounded-xl border p-5 ${card}`} data-testid="ai-first-reply-card">
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>Respuesta inmediata con IA</h2>
          <p className={`mb-4 text-xs ${muted}`}>
            Cuando llega un ticket nuevo, la IA redacta una primera respuesta y se publica en el hilo
            del cliente —y por correo— en segundos. Es independiente del Agente IA: esto habla con el
            cliente, aquello escribe código. El cliente ve que la respuesta es automática y que un
            agente continuará con su caso.
          </p>

          <label className={`mb-1 flex items-center gap-2 text-sm ${text}`}>
            <input
              type="checkbox"
              checked={aiFirstReply.enabled}
              onChange={(e) => setAiFirstReply({ enabled: e.target.checked })}
            />
            Responder automáticamente los tickets nuevos
          </label>
          <p className={`mb-4 ml-6 text-xs ${muted}`}>
            Al desactivarlo, la mesa deja de responder de inmediato al instante siguiente: los tickets
            que ya recibieron respuesta no se tocan. Requiere `ANTHROPIC_API_KEY` en el backend; sin
            ella no pasa nada (se registra en el log y nadie recibe respuesta).
          </p>

          <div className="space-y-3">
            <div>
              <span className={`mb-1.5 block text-xs ${muted}`}>Persona / instrucciones de la mesa</span>
              <textarea
                className="w-full rounded-lg border border-gray-300 bg-transparent px-2 py-1.5 text-sm"
                rows={4}
                maxLength={2000}
                placeholder="Ej.: Habla de usted. Menciona que el horario de atención es de 9 a 18 h. No ofrezcas descuentos."
                value={aiFirstReply.persona}
                onChange={(e) => setAiFirstReply({ persona: e.target.value })}
              />
              <p className={`mt-1 text-xs ${muted}`}>
                Se añade al prompt como instrucciones de la mesa. Las reglas de seguridad van después y
                siempre ganan: la IA nunca revela su configuración, nunca habla de otros clientes y
                nunca promete fechas ni precios.
              </p>
            </div>

            <Field
              label="Firma"
              value={aiFirstReply.signature}
              onChange={(v) => setAiFirstReply({ signature: v })}
              placeholder="Agente IA"
              muted={muted}
              txtCls={txtCls}
            />

            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>Modelo</label>
              <select
                className={txtCls}
                value={aiFirstReply.model}
                onChange={(e) => setAiFirstReply({ model: e.target.value })}
              >
                {AI_FIRST_REPLY_MODELS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>Tokens máximos</label>
              <input
                type="number" min={200} max={2000} className={txtCls}
                value={aiFirstReply.maxTokens}
                onChange={(e) => setAiFirstReply({ maxTokens: Number(e.target.value) || 700 })}
              />
            </div>

            {/* Conversación continua (#509): el agente también responde los
                comentarios públicos del cliente. Usa esta misma persona, firma,
                modelo y exclusiones. */}
            <div className="rounded-lg border border-dashed border-gray-300 p-3" data-testid="ai-conversation-settings">
              <label className={`mb-1 flex items-center gap-2 text-sm ${text}`}>
                <input
                  type="checkbox"
                  checked={aiFirstReply.conversationEnabled}
                  onChange={(e) => setAiFirstReply({ conversationEnabled: e.target.checked })}
                />
                Seguir la conversación: responder también los mensajes del cliente en el hilo
              </label>
              <p className={`mb-3 ml-6 text-xs ${muted}`}>
                Nunca responde notas internas, mensajes del equipo ni sus propios mensajes. Si el cliente pide hablar con
                una persona, se nota frustrado o se llega a un tope, el agente deja una nota interna con el resumen, avisa
                al asignado y se retira del ticket. Cuando le pide un dato al cliente, el SLA queda en pausa hasta que
                conteste.
              </p>
              <div className="flex flex-wrap items-center gap-3">
                <label className={`w-32 text-xs ${muted}`}>Turnos por ticket</label>
                <input
                  type="number" min={1} max={20} className={txtCls}
                  value={aiFirstReply.conversationMaxTurns}
                  onChange={(e) => setAiFirstReply({ conversationMaxTurns: Number(e.target.value) || 5 })}
                />
                <label className={`w-32 text-xs ${muted}`}>Costo máx. (USD)</label>
                <input
                  type="number" min={0.01} max={20} step={0.01} className={txtCls}
                  value={aiFirstReply.conversationMaxCostUsd}
                  onChange={(e) => setAiFirstReply({ conversationMaxCostUsd: Number(e.target.value) || 0.5 })}
                />
              </div>
            </div>

            <div>
              <span className={`mb-1.5 block text-xs ${muted}`}>Etiquetas SIN IA (nunca se responden)</span>
              <div className="flex flex-wrap gap-2">
                {settings.labels.map((l) => {
                  const active = aiFirstReply.excludedLabels.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      onClick={() => setAiFirstReply({ excludedLabels: toggleIn(aiFirstReply.excludedLabels, l.id) })}
                      className="rounded-full px-2.5 py-1 text-xs font-medium"
                      style={{ color: active ? "#fff" : l.color, backgroundColor: active ? l.color : `${l.color}1a` }}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <span className={`mb-1.5 block text-xs ${muted}`}>Categorías SIN IA</span>
              <div className="flex flex-wrap gap-2">
                {settings.categories.map((c) => {
                  const active = aiFirstReply.excludedCategories.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => setAiFirstReply({ excludedCategories: toggleIn(aiFirstReply.excludedCategories, c.id) })}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${active ? "text-white" : "text-gray-500"}`}
                      style={{ backgroundColor: active ? themeBrand.primary : "#9ca3af1a" }}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <span className={`mb-1.5 block text-xs ${muted}`}>Marcas SIN IA</span>
              <div className="flex flex-wrap gap-2">
                {settings.brands.map((b) => {
                  const active = aiFirstReply.excludedBrands.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      onClick={() => setAiFirstReply({ excludedBrands: toggleIn(aiFirstReply.excludedBrands, b.id) })}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${active ? "text-white" : "text-gray-500"}`}
                      style={{ backgroundColor: active ? themeBrand.primary : "#9ca3af1a" }}
                    >
                      {b.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <button
            onClick={() => save({ aiFirstReply })}
            disabled={saving}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: themeBrand.primary }}
          >
            Guardar respuesta inmediata
          </button>
        </div>

        {/* Repositorios de GitHub — acotan el selector de repos del ticket. El
            servidor SÓLO ofrece estos (más GITHUB_DEFAULT_REPO), nunca todos
            los que alcanza su token. */}
        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>Repositorios de GitHub</h2>
          <p className={`mb-4 text-xs ${muted}`}>
            Repositorios (<code>owner/repo</code>) que esta mesa puede usar para crear o enlazar
            issues desde un ticket. El selector ofrece <strong>solo</strong> estos más el repositorio
            por omisión del despliegue (<code>GITHUB_DEFAULT_REPO</code>): el token del servidor
            suele alcanzar repos que no son de la mesa y no se listan. El servidor <strong>rechaza</strong>{" "}
            crear o enlazar issues en cualquier repositorio fuera de esta lista. Uno por línea.
          </p>
          <textarea
            rows={3}
            className="w-full rounded-lg border border-gray-300 bg-transparent px-2 py-1.5 font-mono text-sm"
            placeholder="Coding-Cloud-Online/WAYAK"
            value={reposText}
            onChange={(e) => setReposText(e.target.value)}
          />
          <button
            onClick={() => save({ githubRepos: parseList(reposText) })}
            disabled={saving}
            className="mt-3 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: themeBrand.primary }}
          >
            Guardar repositorios
          </button>
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <div className="mb-3 flex items-center justify-between">
            <h2 className={`text-sm font-semibold ${text}`}>{t("settings.labels.title")}</h2>
            <button onClick={addLabel} className={`text-sm font-medium text-info`}>{t("settings.labels.add")}</button>
          </div>
          <div className="space-y-2">
            {(settings.labels || []).map((l) => (
              <div key={l.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 border-line`}>
                <span className="inline-flex items-center gap-2 text-sm" style={{ color: l.color }}>
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: l.color }} />
                  {l.label} <span className="text-xs text-gray-400">({l.id})</span>
                </span>
                <button onClick={() => removeLabel(l.id)} className={`text-xs text-error hover:underline`}>{t("settings.labels.delete")}</button>
              </div>
            ))}
          </div>
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("settings.brands.title")}</h2>
          <div className="flex flex-wrap gap-2">
            {(settings.brands || []).map((b) => (
              <span key={b.id} className={`rounded-full px-3 py-1 text-sm bg-subtle text-content-secondary`}>{b.label}</span>
            ))}
          </div>
        </div>

        {/* Etapas del ciclo de desarrollo (#559). Los identificadores son fijos
            —la API máquina y la vista del cliente dependen de ellos—; lo que
            cada mesa decide es cómo se llaman y cuáles usa. */}
        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>Etapas del ciclo de desarrollo</h2>
          <p className={`mb-4 text-xs ${muted}`}>
            La línea de tiempo que ven tu equipo <strong>y tus clientes</strong>. Avanza sola con los eventos reales
            (issue creado, rama, PR, deploy a QA, validación del staff). Puedes renombrarlas y apagar las que tu
            mesa no use —por ejemplo las de QA si no tienes ese ambiente—; el cliente sólo ve etapa y fechas,
            nunca los enlaces técnicos.
          </p>
          <div className="space-y-2">
            {stages.map((s) => (
              <div
                key={s.id}
                className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${
                  "border-line"
                }`}
              >
                <input
                  type="color"
                  aria-label={`Color de ${s.label}`}
                  className="h-7 w-9 cursor-pointer rounded border border-gray-300 bg-transparent"
                  value={s.color || DEV_STAGE_META[s.id]?.color || "#64748B"}
                  onChange={(e) => patchStage(s.id, { color: e.target.value })}
                />
                <input
                  className={txtCls}
                  aria-label={`Nombre de ${DEV_STAGE_META[s.id]?.label || s.id}`}
                  value={s.label}
                  onChange={(e) => patchStage(s.id, { label: e.target.value })}
                />
                <span className={`text-[11px] ${muted}`}>
                  {s.kind === "side" ? "lateral" : "en el carril"}
                </span>
                <label className={`flex items-center gap-1.5 text-xs ${text}`}>
                  <input
                    type="checkbox"
                    checked={s.enabled !== false}
                    disabled={REQUIRED_STAGES.includes(s.id)}
                    onChange={(e) => patchStage(s.id, { enabled: e.target.checked })}
                  />
                  Se usa
                </label>
              </div>
            ))}
          </div>
          <button
            onClick={() => save({ stages })}
            disabled={saving}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: themeBrand.primary }}
          >
            Guardar etapas
          </button>
          <p className={`mt-2 text-[11px] ${muted}`}>
            Etapas activas ahora: {selectableStages({ ...settings, stages }).length} de {stages.length}.
          </p>
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("settings.statuses.title")}</h2>
          <div className="flex flex-wrap gap-2">
            {(settings.statuses || []).map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm" style={{ color: s.color, backgroundColor: `${s.color}1a` }}>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>

        {/* Canal de WhatsApp (#549). La vinculación de grupos vive en su propia
            sección; aquí sólo se decide CÓMO se lee el canal. */}
        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>Canal de WhatsApp (grupos)</h2>
          <p className={`mb-4 text-xs ${muted}`}>
            Soporte por grupos de WhatsApp de cada cliente. Sólo funciona con grupos creados por la
            mesa mediante la API oficial (máximo 8 participantes, requiere Official Business
            Account): los grupos que crearon personas no se pueden leer. Los grupos y sus
            participantes se vinculan en <strong>Grupos de WhatsApp</strong>.
          </p>

          <label className={`mb-3 flex items-center gap-2 text-sm ${text}`}>
            <input
              type="checkbox"
              checked={whatsappGroups.enabled}
              onChange={(e) => setWhatsappGroups({ enabled: e.target.checked })}
            />
            Activar el canal de WhatsApp
          </label>
          <p className={`mb-3 ml-6 text-xs ${muted}`}>
            Apagado, no se procesa ningún mensaje de grupo ni se pueden crear grupos: el
            interruptor apaga el canal de verdad.
          </p>

          <div className="mb-3 flex items-center gap-3">
            <label className={`w-32 text-xs ${muted}`}>Conector</label>
            <select
              className={txtCls}
              aria-label="Conector de WhatsApp"
              value={whatsappGroups.connector}
              onChange={(e) =>
                setWhatsappGroups({ connector: e.target.value as "auto" | "simulator" | "cloud" })
              }
            >
              <option value="auto">Automático (según el despliegue: SD_WA_CONNECTOR)</option>
              <option value="simulator">Simulador (fixtures, sin número real)</option>
              <option value="cloud">API oficial de grupos de WhatsApp</option>
            </select>
          </div>
          <p className={`mb-3 ml-32 pl-3 text-xs ${muted}`}>
            Precedencia: lo que elijas aquí manda; en «Automático» decide la variable
            SD_WA_CONNECTOR del despliegue y, si no existe, el simulador.
          </p>
          {waResolution && (
            <div
              data-testid="wa-connector-resolution"
              className={`mb-3 ml-32 rounded-lg border px-3 py-2 pl-3 text-xs ${
                waResolution.envIgnored ? "border-amber-400 text-amber-600" : "border-line text-content-secondary"
              }`}
            >
              <strong>En uso ahora:</strong> {connectorLabel(waResolution.id)} ·{" "}
              {CONNECTOR_SOURCE_LABEL[waResolution.source] || waResolution.source}
              <div className="mt-1">{waResolution.reason}</div>
            </div>
          )}

          <label className={`mb-1 flex items-center gap-2 text-sm ${text}`}>
            <input
              type="checkbox"
              checked={whatsappGroups.requireContactConfirmation}
              onChange={(e) => setWhatsappGroups({ requireContactConfirmation: e.target.checked })}
            />
            Exigir que un agente confirme a cada participante
          </label>
          <p className={`mb-3 ml-6 text-xs ${muted}`}>
            Recomendado: mientras no se confirme quién es un teléfono, sus mensajes no se convierten
            en solicitudes del cliente. Si lo desactivas, el canal aplica solo el mejor candidato
            —nunca uno de otra compañía— y lo deja anotado como hecho por el conector; dar de alta
            contactos del CRM sigue siendo trabajo de un agente.
          </p>

          <label className={`mb-4 flex items-center gap-2 text-sm ${text}`}>
            <input
              type="checkbox"
              checked={whatsappGroups.askUnknownParticipants}
              onChange={(e) => setWhatsappGroups({ askUnknownParticipants: e.target.checked })}
            />
            Preguntar su nombre y puesto a quien no reconocemos
          </label>
          <p className={`mb-4 ml-6 text-xs ${muted}`}>
            Se pregunta una sola vez por teléfono y lo que conteste se guarda en su ficha, para que
            el agente lo tenga delante al confirmarlo.
          </p>

          {/* #715: respuestas del ticket fuera de la ventana de 24 h de WhatsApp. */}
          <div className={`mb-1 text-sm font-medium ${text}`}>
            Plantilla para avisar fuera de la ventana de 24 h
          </div>
          <p className={`mb-2 text-xs ${muted}`}>
            Las respuestas públicas del ticket (del equipo y de la IA) se publican en el grupo del
            cliente. WhatsApp sólo deja mandar texto libre hasta 24 h después del último mensaje de
            alguien del grupo; pasado eso se manda esta plantilla aprobada en Meta (su variable {"{{1}}"}{" "}
            es la referencia del ticket). Sin plantilla, la respuesta no sale al grupo y queda una
            nota interna en el ticket.
          </p>
          <div className="mb-2 flex flex-wrap gap-2">
            <input
              type="text"
              aria-label="Nombre de la plantilla"
              placeholder="nombre_de_la_plantilla"
              value={whatsappGroups.replyTemplate?.name || ""}
              onChange={(e) => {
                const name = e.target.value.trim();
                setWhatsappGroups({
                  replyTemplate: name
                    ? {
                        name,
                        language: whatsappGroups.replyTemplate?.language || "es_MX",
                        includeReference: whatsappGroups.replyTemplate?.includeReference !== false,
                      }
                    : null,
                });
              }}
              className={`min-w-[14rem] ${txtCls}`}
            />
            <input
              type="text"
              aria-label="Idioma de la plantilla"
              placeholder="es_MX"
              value={whatsappGroups.replyTemplate?.language || ""}
              disabled={!whatsappGroups.replyTemplate}
              onChange={(e) =>
                whatsappGroups.replyTemplate &&
                setWhatsappGroups({
                  replyTemplate: { ...whatsappGroups.replyTemplate, language: e.target.value.trim() },
                })
              }
              className={`w-28 rounded-lg border px-2 py-1.5 text-sm !bg-app !border-line !text-content placeholder:!text-content-faint`}
            />
          </div>
          {!isValidReplyTemplate(whatsappGroups.replyTemplate) && (
              <p className="mb-2 text-xs text-red-600">
                El nombre va en minúsculas, dígitos y «_», y el idioma como «es» o «es_MX»; si no, se
                guarda como «sin plantilla».
              </p>
            )}
          <label className={`mb-4 flex items-center gap-2 text-sm ${text}`}>
            <input
              type="checkbox"
              disabled={!whatsappGroups.replyTemplate}
              checked={whatsappGroups.replyTemplate?.includeReference !== false}
              onChange={(e) =>
                whatsappGroups.replyTemplate &&
                setWhatsappGroups({
                  replyTemplate: { ...whatsappGroups.replyTemplate, includeReference: e.target.checked },
                })
              }
            />
            La variable {"{{1}}"} de la plantilla es la referencia del ticket
          </label>

          <button
            onClick={() => save({ whatsappGroups })}
            disabled={saving}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: themeBrand.primary }}
          >
            Guardar canal de WhatsApp
          </button>
        </div>


        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("settings.slaNotify.title")}</h2>
          <p className={`mb-4 text-xs ${muted}`}>{t("settings.slaNotify.description")}</p>
          <div className="space-y-3">
            <textarea
              className={`${txtCls} h-24 resize-none`}
              placeholder={t("settings.slaNotify.placeholder")}
              value={slaBreachEmails}
              onChange={(e) => setSlaBreachEmails(e.target.value)}
            />
          </div>
          <button
            onClick={() => save({ slaBreachNotifyEmails: (settings.slaBreachNotifyEmails || []) })}
            disabled={saving}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 hover:opacity-90"
            style={{ backgroundColor: appearance.primaryColor || themeBrand.primary }}
          >
            {t("settings.slaNotify.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, muted, txtCls }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; muted: string; txtCls: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <label className={`w-32 text-xs ${muted}`}>{label}</label>
      <input className={txtCls} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
